import type { LabConfig, TestResult } from '../types';
import { computeStats, now } from '../utils';

const GROUP = 'K_crypto';

function makeResult(name: string, samples: number[], extra?: Record<string, unknown>): TestResult {
  return {
    name,
    group: GROUP,
    success: samples.length > 0,
    durationMs: typeof extra?.wallMs === 'number' ? extra.wallMs : samples.reduce((a, b) => a + b, 0),
    samples,
    stats: computeStats(samples),
    metadata: extra,
  };
}

function failResult(name: string, t0: number, error: string): TestResult {
  return { name, group: GROUP, success: false, durationMs: now() - t0, error };
}

/**
 * Measure throughput of an async crypto operation.
 * Runs `totalOps` operations in a tight loop, measures total wall time,
 * computes ops/sec. Repeats `runs` times for statistics.
 */
async function measureCryptoThroughput(
  work: () => Promise<unknown>,
  totalOps: number,
  options: { runs: number; warmup: number }
): Promise<{ samples: number[]; wallMs: number }> {
  const { runs, warmup } = options;
  // Warmup
  for (let i = 0; i < warmup; i++) {
    let count = 0;
    const t0 = now();
    while (count < totalOps) {
      await work();
      count++;
    }
  }
  // Measurements
  const samples: number[] = [];
  const wallStart = now();
  for (let i = 0; i < runs; i++) {
    let count = 0;
    const t0 = now();
    while (count < totalOps) {
      await work();
      count++;
    }
    const elapsedMs = now() - t0;
    samples.push(elapsedMs > 0 ? (count / elapsedMs) * 1000 : 0); // ops/sec
  }
  const wallMs = now() - wallStart;
  return { samples, wallMs };
}

export async function runCryptoTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];
  const isProd = config.mode === 'prod';

  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return [failResult('all', t0, 'Web Crypto API not available')];
  }

  const runs = isProd ? 5 : 8;
  const warmup = 2;

  // Prepare test data
  const data256 = new Uint8Array(256);
  const data1024 = new Uint8Array(1024);
  const key256 = new Uint8Array(32);
  const key128 = new Uint8Array(16);
  const iv12 = new Uint8Array(12);
  for (let i = 0; i < data256.length; i++) data256[i] = (i * 37 + 13) & 0xff;
  for (let i = 0; i < data1024.length; i++) data1024[i] = (i * 53 + 7) & 0xff;
  for (let i = 0; i < key256.length; i++) key256[i] = (i * 41 + 19) & 0xff;
  for (let i = 0; i < key128.length; i++) key128[i] = (i * 29 + 11) & 0xff;
  for (let i = 0; i < iv12.length; i++) iv12[i] = (i * 17 + 3) & 0xff;

  // Total ops per measurement run — tuned so each measurement takes ~30-50ms
  const ops = isProd ? 500 : 1500;

  // --- AES-GCM encrypt (256-bit) ---
  try {
    const aesKey = await crypto.subtle.importKey('raw', key256, { name: 'AES-GCM' }, false, ['encrypt']);
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv12 }, aesKey, data256),
      ops, { runs, warmup }
    );
    tests.push(makeResult('aes_gcm_encrypt', samples, { wallMs, operations: ops, dataSize: 256, keyBits: 256 }));
  } catch (err) {
    tests.push(failResult('aes_gcm_encrypt', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- AES-GCM decrypt (256-bit) ---
  try {
    const aesKey = await crypto.subtle.importKey('raw', key256, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv12 }, aesKey, data256);
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv12 }, aesKey, ciphertext),
      ops, { runs, warmup }
    );
    tests.push(makeResult('aes_gcm_decrypt', samples, { wallMs, operations: ops, dataSize: 256, keyBits: 256 }));
  } catch (err) {
    tests.push(failResult('aes_gcm_decrypt', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- AES-CBC encrypt (128-bit) ---
  try {
    const aesKey = await crypto.subtle.importKey('raw', key128, { name: 'AES-CBC' }, false, ['encrypt']);
    const iv16 = new Uint8Array(16);
    for (let i = 0; i < 16; i++) iv16[i] = (i * 23 + 5) & 0xff;
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.encrypt({ name: 'AES-CBC', iv: iv16 }, aesKey, data256),
      ops, { runs, warmup }
    );
    tests.push(makeResult('aes_cbc_encrypt', samples, { wallMs, operations: ops, dataSize: 256, keyBits: 128 }));
  } catch (err) {
    tests.push(failResult('aes_cbc_encrypt', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- SHA-256 ---
  try {
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.digest('SHA-256', data1024),
      ops, { runs, warmup }
    );
    tests.push(makeResult('sha256', samples, { wallMs, operations: ops, dataSize: 1024 }));
  } catch (err) {
    tests.push(failResult('sha256', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- SHA-1 ---
  try {
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.digest('SHA-1', data1024),
      ops, { runs, warmup }
    );
    tests.push(makeResult('sha1', samples, { wallMs, operations: ops, dataSize: 1024 }));
  } catch (err) {
    tests.push(failResult('sha1', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- HMAC-SHA256 ---
  try {
    const hmacKey = await crypto.subtle.importKey('raw', key256, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.sign('HMAC', hmacKey, data1024),
      ops, { runs, warmup }
    );
    tests.push(makeResult('hmac_sha256', samples, { wallMs, operations: ops, dataSize: 1024 }));
  } catch (err) {
    tests.push(failResult('hmac_sha256', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- PBKDF2 (pure CPU-bound, no hardware accel) ---
  try {
    const pbkdf2Key = await crypto.subtle.importKey('raw', key128, 'PBKDF2', false, ['deriveBits']);
    const pbkdf2Salt = new Uint8Array(16);
    for (let i = 0; i < 16; i++) pbkdf2Salt[i] = (i * 43 + 9) & 0xff;
    const pbkdf2Iter = 10000;
    const { samples, wallMs } = await measureCryptoThroughput(
      () => crypto.subtle.deriveBits(
        { name: 'PBKDF2', salt: pbkdf2Salt, iterations: pbkdf2Iter, hash: 'SHA-256' },
        pbkdf2Key, 256
      ),
      10, { runs, warmup }
    );
    tests.push(makeResult('pbkdf2', samples, { wallMs, iterations: pbkdf2Iter, keyBits: 256 }));
  } catch (err) {
    tests.push(failResult('pbkdf2', t0, err instanceof Error ? err.message : String(err)));
  }

  // --- ECDH P-256 key exchange ---
  try {
    const ecdhCycles = isProd ? 5 : 15;
    const { samples, wallMs } = await measureCryptoThroughput(
      async () => {
        const keyPair = await crypto.subtle.generateKey(
          { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']
        );
        const pubJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
        const pubKey = await crypto.subtle.importKey('jwk', pubJwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
        await crypto.subtle.deriveBits({ name: 'ECDH', public: pubKey }, keyPair.privateKey, 256);
      },
      ecdhCycles, { runs, warmup }
    );
    tests.push(makeResult('ecdh_p256', samples, { wallMs, curve: 'P-256', cyclesPerOp: ecdhCycles }));
  } catch (err) {
    tests.push(failResult('ecdh_p256', t0, err instanceof Error ? err.message : String(err)));
  }

  return tests;
}
